<script setup>

import { ref, computed, inject, watch, provide, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { 
	apiGetModele,
	apiBasculerAffichageElementsMasques,
	apiGetDescriptionBriques,
	apiSupprimerBriquesRapport,
	apiOuvrirJeuDonnees,
	apiEnregistrer,
	apiAnnulerDerniereCommande,
	apiRefaireProchaineCommande,
	apiModifierInfosChamp,
	apiModifierValeurs,
	apiSupprimerObjets } from '@/services/ServiceModele.js';
  import {
	getPositionBriqueRapport,
	getPositionElement,
	getPositionSection,
	getCalculable,
	getMenuOuvertAjout } from '@/scripts/global.js';

import {
	getElement,
	getElementEtParent,
	trouverElementFrere,
	trouverElementParent,
	getSection } from '@/scripts/navigationRapport.js'

import {
	getTextesBriquesACopier,
	copierDansPressePapier,
	getAppelApiColler } from '@/scripts/gestionPressePapier.js'

import PanelGauche from './gauche/PanelGauche.vue';
import PanelCentre from './centre/PanelCentre.vue';
import PanelDroit from './droite/PanelDroit.vue';
import FenetreFormule from '@/components/modales/FenetreFormule.vue';
import ModaleDefinitionJeuDonnees from '@/components/modales/ModaleDefinitionJeuDonnees.vue';
import ModaleDerivationJeuDonnees from '@/components/modales/ModaleDerivationJeuDonnees.vue';

const CLE_DERNIERE_SEL = 'derniere-selection';

const modifierLibellePage = inject('modifierLibellePage');
const setMessageChargement = inject('setMessageChargement');
const gererErreurApi = inject('gererErreurApi');
const afficherSucces = inject('afficherSucces');
const afficherInfo = inject('afficherInfo');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const appelerApiTelechargement = inject('appelerApiTelechargement');

const modele = ref(null);
const evenement = ref({});
const numOngletsSchemaSelectionnes = ref({
	numOngletPrincipal: 0, 
	numSousOngletDimEtParam: 0,
	numSousOngletImport: 0,
	numSousOngletProgrammabilite: 0,
	numSousOngletAutres: 0
});
const selectionSchema = ref({
	typeObjet: null,
	champs: [],
	dimension: null,
	parametre: null,
	constante: null,
	listeValeurs: null,
	import: null,
	routine: null
});

const conteneursFormulesAffichees = ref([]);
const sectionSelectionnee = ref(null);
const elementsSelectionnes = ref([]);
const premierElementSelectionne = ref(null);
const calculablesSelectionnes = ref([]);
const valeurSelectionnee = ref(null);
const dernierFichierSelectionne = ref(null);

const affichageModaleDefinitionJeuDonnees = ref(false);
const typeSourceJeuDonnees = ref(null);

onMounted(() => {
	setMessageChargement('Chargement du modèle en cours...');
	apiGetModele(useRoute().params.idModele).then(
		reponse => {
			console.log(reponse);
			modele.value = reponse;
			setMessageChargement(null);
			selectionSchema.value.typeObjet = modele.value.schema.typesObjet.length ? modele.value.schema.typesObjet[0] : null;
			selectionnerDernierElementSelectionne();
			nextTick(() => donnerFocusAuRapport());
		}, 
		err => {
			setMessageChargement(null);
			gererErreurApi(err);
    });
});

watch(() => modele.value?.infosModele?.nomModele, (nomModele) => {
  modifierLibellePage(nomModele);
});

const typesDonneePrimitifs = [
		{classe: 'PRIMITIF', id: 0, libelle: 'Entier'},
		{classe: 'PRIMITIF', id: 1, libelle: 'Entier long'},
		{classe: 'PRIMITIF', id: 2, libelle: 'Nombre réel'},
		{classe: 'PRIMITIF', id: 3, libelle: 'Date'},
		{classe: 'PRIMITIF', id: 4, libelle: 'Texte'},
		{classe: 'PRIMITIF', id: 5, libelle: 'Booléen'}
	];

const typesDonneeObjets = computed(() => {
	return modele.value?.schema.typesObjet.filter(typeObj => typeObj.typeOccurrence == 'PLUSIEURS').map(typeObjet => { 
		return {classe: 'OBJET', id: typeObjet.id, libelle: typeObjet.nom}
	});
});

const typesDonneeListesValeurs = computed(() => {
	return modele.value?.schema.listesValeurs.map(listeValeurs => { 
		return {classe: 'VALEUR_LISTE', id: listeValeurs.id, libelle: listeValeurs.nom}
	});
});

const typesObjetUniques = computed(() => {
    return schema.value.typesObjet.filter(typeObjet => typeObjet.typeOccurrence === 'UNE');
});

const typesObjetMultiples = computed(() => {
    return schema.value.typesObjet.filter(typeObjet => typeObjet.typeOccurrence === 'PLUSIEURS');
});

const schema = computed(() => {
	return modele.value?.schema;
});

const dimensions = computed(() => {
	return modele.value?.schema?.dimensions;
});

const couleurs = computed(() => {
	return modele.value?.rapport?.couleurs;
});

watch(() => modele.value?.basculeAffichageElementsMasques, (bascule) => {
	if (bascule) {
		delete modele.value?.basculeAffichageElementsMasques;
		basculerAffichageElementsMasques();
	}
});

function appelerApiModele(fonctionApi, argumentsApi, fonctionReponse, messageChargement) {
  console.log(`Appel de l'API ${fonctionApi.name}...`);
  if (!messageChargement) {
	messageChargement = 'Chargement en cours...';
  }
  setMessageChargement(messageChargement);
  argumentsApi.unshift(modele.value);
  fonctionApi.apply(null, argumentsApi).then(
		reponse => {
			console.log(reponse);
      		fonctionReponse(true, reponse);
			setMessageChargement(null);
    	}, err => {
			setMessageChargement(null);
      		let erreurMuette = fonctionReponse(false, err);
			if (!erreurMuette) {
				gererErreurApi(err);
			}
		});
}

function appelerApiModifModele(fonctionApi) {
	const argumentsApi = Array.from(arguments).slice(1);
	var fonctionApresReponse = null;
	const dernierArgument = argumentsApi[argumentsApi.length - 1];
	if (typeof dernierArgument === 'function') {
		fonctionApresReponse = dernierArgument;
		argumentsApi.pop();
	}
	const fonctionReponse = function(reponseOK, modifications) {
		if (reponseOK) {
			mettreAJourModele(modifications);
			if (fonctionApresReponse != null) {
				nextTick(() => fonctionApresReponse(true));
			}
		} else {
			if (fonctionApresReponse != null) {
				fonctionApresReponse(false);
			}
		}
	};
	appelerApiModele(fonctionApi, argumentsApi, fonctionReponse, 'Modification en cours...');
}

function appelerApiTelechargementModele(fonctionApi) {
	const argumentsApi = Array.from(arguments).slice(1);
  	var fonctionReponse = null;
  	const dernierArgument = argumentsApi[argumentsApi.length - 1];
  	if (typeof dernierArgument === 'function') {
    	fonctionReponse = dernierArgument;
    	argumentsApi.pop();
  	}
	argumentsApi.unshift(modele.value);
	appelerApiTelechargement(fonctionApi, argumentsApi, fonctionReponse);
}

function basculerAffichageElementsMasques() {
  appelerApiModifModele(apiBasculerAffichageElementsMasques);
}

const affichageElementsMasques = computed(() => {
	return modele.value.rapport.affichageElementsMasques;
});

const classeMasquage = computed(() => {
	return (affichageElementsMasques.value ? 'masquage-transparent' : 'masquage-total');
});

function getTypeObjet(idTypeObjet, nomTypeObjet) {
	if (idTypeObjet) {
		return modele.value.schema.typesObjet.find(typeObjet => typeObjet.id === idTypeObjet);
	} else if (nomTypeObjet) {
		return modele.value.schema.typesObjet.find(typeObjet => typeObjet.nom === nomTypeObjet);
	} else {
		return modele.value.schema.typeObjetMaitre;
	}
}

function getPopulation(idPopulation) {
	return modele.value?.schema.typesObjet.find(typeObjet => typeObjet.population?.id === idPopulation).population;
}

function typeObjetACleAuto(typeObjet) {
	return typeObjet.typeOccurrence === 'PLUSIEURS' && getChampsCles(typeObjet)[0].modeEvaluation === 'CLE_AUTO';
}

function getParametre(idParametre) {
	return modele.value?.schema.parametres.find(param => param.id === idParametre);
}

function getChamp(idTypeObjet, idChamp) {
	return getTypeObjet(idTypeObjet).champs.find(champ => champ.id === idChamp);
}

function getChampsCles(typeObjet) {
	if (typeObjet?.idChampsCles) {
		return typeObjet.idChampsCles.map(idChamp => typeObjet.champs.find(champ => champ.id === idChamp));
	} else {
		return [];
	}
}

function getDimension(idDimension) {
	return modele.value?.schema.dimensions.find(dim => dim.id === idDimension);
}

function getListeValeurs(idListeValeurs) {
	return modele.value?.schema.listesValeurs.find(listeValeurs => listeValeurs.id === idListeValeurs);
}

function getImport(idImport) {
	return modele.value?.schema.imports.find(imp => imp.id === idImport);
}

function getImportFichier(idFichierImport) {
	return modele.value.donnees.imports.find(imp => imp.id === idFichierImport);
}

function getTypeDonnee(typeDonnee) {
	switch(typeDonnee?.classe) {
			case 'PRIMITIF':
				return typesDonneePrimitifs.find(type => type.id === typeDonnee.id);
			case 'VALEUR_LISTE':
				return typesDonneeListesValeurs.value.find(type => type.id === typeDonnee.id);
			case 'OBJET':
				return typesDonneeObjets.value.find(type => type.id === typeDonnee.id);
			default:
				return null;
		}
}

function getTypeFormat(idTypeFormat) {
	return idTypeFormat != null ? modele.value?.schema.typesFormat.find(typeFormat => typeFormat.id == idTypeFormat) : null;
}

function getTypesFormat(typeDonnee) {
	if (typeDonnee.classe == 'PRIMITIF') {
		const idTypesFormat = modele.value?.schema.typesFormatParTypeDonnee.find(t => t.typeDonnee.classe === 'PRIMITIF' && t.typeDonnee.id === typeDonnee.id)?.idTypesFormat;
		if (idTypesFormat != null) {
			return idTypesFormat.map(idTypeFormat => getTypeFormat(idTypeFormat));
		} else {
			return null;
		}
	} else {
		return null;
	}
}

function getObjets(idTypeObjet) {
	return modele.value.donnees.typesObjet.find(t => t.idTypeObjet === idTypeObjet).objets;
}

function selectionnerChamp(typeObjet, champ) {
	numOngletsSchemaSelectionnes.value.numOngletPrincipal = 0;
	selectionSchema.value.typeObjet = typeObjet;
	if (champ) {
		selectionSchema.value.champs = [champ];
	} else {
		selectionSchema.value.champs = [];
	}
}

function selectionnerDimension(dimension) {
	selectionSchema.value.dimension = dimension;
	numOngletsSchemaSelectionnes.value.numOngletPrincipal = 1;
	numOngletsSchemaSelectionnes.value.numSousOngletDimEtParam = 0;
}

function selectionnerListeValeurs(listeValeurs) {
	selectionSchema.value.listeValeurs = listeValeurs;
	numOngletsSchemaSelectionnes.value.numOngletPrincipal = 4;
	numOngletsSchemaSelectionnes.value.numSousOngletAutres = 1;
}

function selectionnerImport(importDonnees) {
	selectionSchema.value.import = importDonnees;
	numOngletsSchemaSelectionnes.value.numOngletPrincipal = 2;
	numOngletsSchemaSelectionnes.value.numSousOngletImport = 0;
}

function selectionnerRoutine(nomRoutine) {
	selectionSchema.value.routine = modele.value.schema.routines.find(routine => routine.nom === nomRoutine);
	numOngletsSchemaSelectionnes.value.numOngletPrincipal = 3;
	numOngletsSchemaSelectionnes.value.numSousOngletProgrammabilite = 0;
}

function selectionnerCalculable(positionBrique, expression, nomVariable) {
	calculablesSelectionnes.value = [{
		positionBrique: positionBrique,
		expression: expression,
		nomVariable: nomVariable
	}];
}

function editerExpression() {
	if (calculablesSelectionnes.value.length) {
		nextTick(() => evenement.value.editionExpression = true);
	}
}

function selectionnerValeur(valeur) {
	valeurSelectionnee.value = valeur;
	if (valeur?.coord != null) {
		selectionnerElementSchema(valeur.coord);
	}
}

function selectionnerSection(positionSection, editionTitre) {
	sectionSelectionnee.value = getSection(positionSection, modele.value.rapport.sectionPrincipale);
	selectionnerElement(sectionSelectionnee.value.titre, sectionSelectionnee.value);
	if (editionTitre) {
		sectionSelectionnee.value.titre.modeEdition = true;
	}
	donnerFocusAuRapport();
}

function selectionnerElement(element, elementParent) {
	viderElementsSelectionnes();
	afficherBarreFlottante(null);
	if (element != null) {
		activerSelectionElement(element, elementParent);
		afficherInfosElementsSelectionnes();
		memoriserDernierElementSelectionne(element, elementParent);
	}
}

function memoriserDernierElementSelectionne(element, elementParent) {
	const positionSelection = {
		positionSection: getPositionSection(element, elementParent),
		positionDansSection: getPositionElement(element, elementParent)
	};
	sessionStorage.setItem(CLE_DERNIERE_SEL, JSON.stringify(positionSelection));
}

function selectionnerPlageElements(element, elementParent) {
	if (elementsSelectionnes.value.length === 0) {
		selectionnerElement(element, elementParent);		
	} else {
		const dernierElementSelectionne = elementsSelectionnes.value[0];
		if (dernierElementSelectionne.elementParent.positionDansSection == elementParent.positionDansSection) {
			if (elementParent.type == 'TABLEAU') {
				const ligneMin = Math.min(element.ligne, dernierElementSelectionne.ligne);
				const ligneMax = Math.max(element.ligne, dernierElementSelectionne.ligne);
				const colMin = Math.min(element.colonne, dernierElementSelectionne.colonne);
				const colMax = Math.max(element.colonne, dernierElementSelectionne.colonne);
				const cellulesPlage = elementParent.cellules.filter(c => c.ligne >= ligneMin && c.ligne <= ligneMax && c.colonne >= colMin && c.colonne <= colMax);
				cellulesPlage.forEach(cellule => activerSelectionElement(cellule, elementParent));
				activerSelectionElement(element, elementParent);
				afficherInfosElementsSelectionnes();
			}
		}
	}
}

function basculerSelection(element, elementParent) {
	if (element.selectionne) {
		elementsSelectionnes.value.splice(elementsSelectionnes.value.indexOf(element), 1);
		desactiverSelectionElement(element);
	} else {
		if (elementsSelectionnes.length === 0) {
			selectionnerElement(element, elementParent);
		} else {
			activerSelectionElement(element, elementParent);
		}
	}
}

function activerSelectionElement(element, elementParent) {
	if (!element.selectionne) {
		if (elementsSelectionnes.value.length > 0) {
			delete elementsSelectionnes.value[0].dernierElementSelectionne;
		}
		elementsSelectionnes.value.unshift(element);
		element.selectionne = true;
		element.dernierElementSelectionne = true;
		delete element.survol;
		element.elementParent = elementParent;
	} else {
		if (elementsSelectionnes.value.length > 0 && elementsSelectionnes.value[0] != element) {
			delete elementsSelectionnes.value[0].dernierElementSelectionne;
			const indiceElement = elementsSelectionnes.value.indexOf(element);
			elementsSelectionnes.value.splice(indiceElement, 1);
			elementsSelectionnes.value.unshift(element);
			element.dernierElementSelectionne = true;
		}
	}
}

function desactiverSelectionElement(element) {
	if (element.selectionne) {
		delete element.selectionne;
		delete element.elementParent;
		delete element.dernierElementSelectionne;
	}
}

function viderElementsSelectionnes() {
	if (elementsSelectionnes.value.length > 0) {
		elementsSelectionnes.value.forEach(elt => desactiverSelectionElement(elt));
		elementsSelectionnes.value.length = 0;
	}
}

function selectionnerDernierElementSelectionne() {	
	const texteDernierePositionMemorisee = sessionStorage.getItem(CLE_DERNIERE_SEL);
	let elementEtParent = null;
	let section = null;
	if (texteDernierePositionMemorisee) {
		const dernierePositionMemorisee = JSON.parse(texteDernierePositionMemorisee);
		while (!elementEtParent?.element) {
			elementEtParent = trouverElementEtParent(dernierePositionMemorisee.positionSection, dernierePositionMemorisee.positionDansSection);
			if (!elementEtParent?.element) {
				let taillePositionDansSection = dernierePositionMemorisee.positionDansSection.length;
				if (taillePositionDansSection === 0) {
					dernierePositionMemorisee.positionSection.pop();
					dernierePositionMemorisee.positionDansSection = [-1];
				} else {
					let positionDansSectionFinale = dernierePositionMemorisee.positionDansSection[taillePositionDansSection - 1];
					if (positionDansSectionFinale > 0) {
						dernierePositionMemorisee.positionDansSection[taillePositionDansSection - 1] = positionDansSectionFinale - 1;
					} else if (positionDansSectionFinale === 0) {
						if (taillePositionDansSection > 1) {
							dernierePositionMemorisee.positionDansSection.pop();
						} else {
							dernierePositionMemorisee.positionDansSection[taillePositionDansSection - 1] = -1;
						}
					} else {	// positionDansSectionFinale === -1 : titre
						let taillePositionSection = dernierePositionMemorisee.positionSection.length;
						if (taillePositionDansSection === 1 && taillePositionSection > 0) {
							dernierePositionMemorisee.positionSection.pop();
						} else {
							console.log('Position impossible');
							console.log(dernierePositionMemorisee);
							return;
						}
					}
				}
			}
		}
		if (dernierePositionMemorisee.positionDansSection.length) {
			section = getSection(dernierePositionMemorisee.positionSection, modele.value.rapport.sectionPrincipale);
		} else if (dernierePositionMemorisee.positionSection.length) {
			section = getSection(dernierePositionMemorisee.positionSection.slice(0, -1), modele.value.rapport.sectionPrincipale);
		} else {
			console.log('Position section impossible');
			console.log(dernierePositionMemorisee);

		}
	} else {
		section = modele.value.rapport.sectionPrincipale;
		elementEtParent = trouverElementEtParent([], [-1]);
	}
	sectionSelectionnee.value = section;
	selectionnerElement(elementEtParent.element, elementEtParent.parent);
}

function afficherInfosElementsSelectionnes() {
	const elements = elementsSelectionnes.value;
	calculablesSelectionnes.value = elements.map(elt => getCalculable(elt, elt.elementParent)).filter(calc => calc);
	premierElementSelectionne.value = (elements.length > 0 ? elements[0] : null);
	switch(premierElementSelectionne.value?.type) {
		case 'ValeurExpression':
		case 'ValeurAgregatTotal':
		case 'ValeurAgregatGroupe':
		case 'EnteteObjet':
		case 'ValeurSerie':
			selectionnerValeur(premierElementSelectionne.value.valeur);
			break;
		default:
			selectionnerValeur(null);
	}
}

function selectionnerElementSchema(coord) {
	if (coord.idChamp !== null) {
		selectionnerChamp(getTypeObjet(coord.idTypeObjet), getChamp(coord.idTypeObjet, coord.idChamp));
	} else if (coord.idDimension !== null) {
		selectionnerDimension(getDimension(coord.idDimension));
	} else if (coord.idPopulation !== null) {
		selectionnerChamp(getTypeObjet(coord.idTypeObjet), getPopulation(coord.idPopulation));
	}
}

function editerFormule(element) {
	const coord = element?.valeur?.coord;
	if (!coord) {
		return;
	}
	var champ;
	if (coord.idChamp) {
		champ = getChamp(coord.idTypeObjet, coord.idChamp);
	} else if (coord.idDimension) {
		champ = getDimension(coord.idDimension);
	}
	if (champ) {
		if (champ.modeEvaluation === 'INDEFINI') {
			const champModifie = {};
			Object.assign(champModifie, champ);
			champModifie.modeEvaluation = 'CALCUL';
			appelerApiModifModele(apiModifierInfosChamp, champModifie, (reponseOK) => {
				if (reponseOK) {
					ouvrirFenetreFormule(champ, true);
				}
			});
		} else if (champ.modeEvaluation === 'CALCUL') {
			ouvrirFenetreFormule(champ, true);
		}
	}
}

function mettreAJourModele(modificationsModele) {
	mettreAJourObjet(modele.value, modificationsModele.modele);
	selectionnerDernierElementSelectionne();
	modele.value.rapport.focus = true;
}

function mettreAJourObjet(objet, objetModifie) {
	if (objet == null) {
		console.log('Problème lors de la mise à jour de l\'objet suivant (objet existant = null) :');
		console.log(objetModifie);
	}
	Object.keys(objetModifie).forEach(attribut => {
		let valeurExistante = objet[attribut];
		let modification = objetModifie[attribut];
		if (valeurExistante == undefined || modification === null) {
			objet[attribut] = modification;
		} else if (Array.isArray(valeurExistante)) {
			if (Array.isArray(modification)) {
				objet[attribut] = modification;
			} else {
				mettreAJourListe(valeurExistante, modification);
			}
		} else if (typeof valeurExistante === 'object') {
			mettreAJourObjet(valeurExistante, modification);
		} else {
			objet[attribut] = modification;
		}
	});
}

function mettreAJourListe(liste, modificationListe) {
	for (const modification of modificationListe.modifications) {
		switch (modification.type) {
			case 'AJOUT': {
				liste.splice(modification.indice, 0, modification.objet);
				break;
			}
			case 'REMPL': {
				let element = liste[modification.indice];
				Object.keys(element).forEach(attribut => delete element[attribut]);
				mettreAJourObjet(element, modification.objet);
				break;
			}
			case 'MODIF': {
				let element = liste[modification.indice];
				mettreAJourObjet(element, modification.objet);
				break;
			}
			case 'SUPPR': {
				liste.splice(modification.indice, 1);
				break;
			}
		}
	}
}

function annulerDerniereCommande() {
  appelerApiModifModele(apiAnnulerDerniereCommande);
}

function refaireProchaineCommande() {
  appelerApiModifModele(apiRefaireProchaineCommande);
}

function ouvrirJeuDonnees(jeuDonnees) {
	console.log('Ouverture jeu :');
	console.log(jeuDonnees);
	appelerApiModele(apiOuvrirJeuDonnees, [jeuDonnees], function(reponseOK, nouveauModele) {
        if (reponseOK) {
            modele.value = nouveauModele;
			selectionnerDernierElementSelectionne();
			donnerFocusAuRapport();
			if (modele.value.donnees.jeuDonneesCourant.statut === 'INITIAL') {
				affichageModaleDefinitionJeuDonnees.value = true;
			}
		}
    });
}

function enregistrerSous(nomModele, ecraserSiExistant) {
	console.log(`Enregistrement de ${nomModele}`);
	setMessageChargement('Enregistrement du modèle en cours...');
	apiEnregistrer(modele.value, nomModele, ecraserSiExistant).then(
		infosModele => {
			setMessageChargement(null);
			if (infosModele) {
				Object.assign(modele.value.infosModele, infosModele);
	      		modele.value.affichageModaleEnregistrerSous = false;
				  afficherSucces('Modèle enregistré');
			} else {
				// Confirmation nécessaire
				if (confirm("Un modèle de ce nom existe déjà. Souhaitez-vous l'écraser ?")) {
					enregistrerSous(nomModele, true);
				}
			}
		}, 
		err => {
			setMessageChargement(null);
			gererErreurApi(err);
		});
}

function copierElements() {
	const textesSimplesEtHtml = getTextesBriquesACopier(elementsSelectionnes.value);
	const positionsBriques = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent));	
	appelerApiModele(apiGetDescriptionBriques, [positionsBriques], (reponseOK, texteJson) => {
		if (reponseOK) {
				copierDansPressePapier(textesSimplesEtHtml.texteSimple, textesSimplesEtHtml.texteHtml, texteJson).then(() => {
				afficherInfo('Éléments copiés dans le presse-papier');
			});
		}
	});
}

function couperElements() {
	const textesSimplesEtHtml = getTextesBriquesACopier(elementsSelectionnes.value);
	const positionsBriques = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent));	
	appelerApiModele(apiGetDescriptionBriques, [positionsBriques], (reponseOK, texteJson) => {
		if (reponseOK) {
				copierDansPressePapier(textesSimplesEtHtml.texteSimple, textesSimplesEtHtml.texteHtml, texteJson).then(() => {
				supprimerElementsSelectionnes();
				afficherInfo('Éléments coupés dans le presse-papier');
			});
		}
	});
}

function collerElements(element) {
	getAppelApiColler(element, modele.value.rapport).then((appelApi) => {
		if (appelApi) {
			appelerApiModifModele(appelApi.api, appelApi.argument);
		}
	});
}

function supprimerElementsSelectionnes() {
  console.log('Suppression d\'éléments');
	if (elementsSelectionnes.value.length == 0) {
		return;
	}
	const objets = elementsSelectionnes.value.filter(elt => elt.type === 'EnteteObjet').map(elt => elt.objet);
	if (objets.length > 0) {
		const idTypeObjet = objets[0].idTypeObjet;
		const idObjets = objets.filter(obj => obj.idTypeObjet === idTypeObjet).map(obj => obj.idObjet);
		appelerApiModifModele(apiSupprimerObjets, idTypeObjet, idObjets);
		return;
	}
	const coordValeurs = elementsSelectionnes.value.map(elt => elt.valeur?.coord).filter(coord => coord != null);
	if (coordValeurs.length > 0) {
		const modifsValeurs = coordValeurs.map(coord => { 
			return {coord: coord, valeur: null};
		});
		appelerApiModifModele(apiModifierValeurs, modifsValeurs);
	} else {
		const positionsBriques = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent));
		appelerApiModifModele(apiSupprimerBriquesRapport, positionsBriques);
	}
}

function gererToucheClavierModele(event) {
  	switch (event.key.toUpperCase()) {
		case 'Z':       // Ctrl + Z pour annuler dernière commande
			if (event.ctrlKey) {
			event.preventDefault();
			event.stopPropagation();
			annulerDerniereCommande();
			}
			break;
		case 'Y':       // Ctrl + Y pour refaire prochaine commande
			if (event.ctrlKey) {
			event.preventDefault();
			event.stopPropagation();
			refaireProchaineCommande();
			}
			break;
		case 'S':       // Ctrl + S pour sauvegarder
			if (event.ctrlKey) {
			event.preventDefault();
			event.stopPropagation();
			if (modele.value.infosModele.dateDernierEnregistrement != null) {
				enregistrerSous(modele.value.infosModele.nomModele, true);
			} else {
				modele.value.affichageModaleEnregistrerSous = true;
			}
			}
			break;
  	}
}

function gererToucheClavierRapport(event) {
	if (modele.value.rapport.focus && elementsSelectionnes.value.length > 0) {
		const element = elementsSelectionnes.value[0];
		const elementEtParent = {element: element, parent: element.elementParent};
		var elementEtParentCible = null;
		switch (event.key) {
			case ' ': // Barre d'espace pour ouvrir le menu déroulant			
				element.ouvertureMenu = [];
				event.preventDefault();
				event.stopPropagation();
				break;
			case 'Enter': // Entrée pour éditer
				element.modeEdition = true;
				event.preventDefault();
				event.stopPropagation();
				break;
			case 'Escape': // Echap pour se rendre au niveau parent
				element.selectionElementParent = true;
				event.preventDefault();
				event.stopPropagation();
				break;
			case 'Delete': // Suppr pour supprimer
				supprimerElementsSelectionnes();
				event.preventDefault();
				event.stopPropagation();
				break;
			case 'x':       // Ctrl + X pour couper
				if (event.ctrlKey) {
					event.preventDefault();
					event.stopPropagation();
					couperElements();
				}
				break;
			case 'c':       // Ctrl + C pour copier
				if (event.ctrlKey) {
					event.preventDefault();
					event.stopPropagation();
					copierElements();
				}
				break;
			case 'v':       // Ctrl + V pour coller
				if (event.ctrlKey) {
					event.preventDefault();
					event.stopPropagation();
					if (elementsSelectionnes.value.length > 0)
						collerElements(elementsSelectionnes.value[0]);
				}
				break;
			case 'e':       // Ctrl + E pour éditer l'expression
				if (event.ctrlKey) {
					event.preventDefault();
					event.stopPropagation();
					editerExpression();
				}
				break;
			case '+':     // Ctrl + pour ajouter après
				if (event.ctrlKey) {
				const menuOuvert = getMenuOuvertAjout(element, 'apres');
				if (menuOuvert != null) {
					element.ouvertureMenu = menuOuvert;
					event.preventDefault();
					event.stopPropagation();
				}
				}
				break;
			case '-':     // Ctrl - pour ajouter avant
				if (event.ctrlKey) {
					const menuOuvert = getMenuOuvertAjout(element, 'avant');
					if (menuOuvert != null) {
						element.ouvertureMenu = menuOuvert;
						event.preventDefault();
						event.stopPropagation();
					}
				}
				break;
			case '=':     // = pour éditer la formule
				editerFormule(element);
				event.preventDefault();
				event.stopPropagation();
				break;
			case 'ArrowUp':
			case 'ArrowLeft':
			case 'ArrowDown':
			case 'ArrowRight':
			case 'PageDown':
			case 'PageUp':
				elementEtParentCible = trouverElementEtParentCible(elementEtParent, event);
				event.preventDefault();
				event.stopPropagation();
				break;
		}
		if (elementEtParentCible != null) {
			selectionnerElement(elementEtParentCible.element, elementEtParentCible.parent);
		}
	}
}

function trouverSection(positionSection) {
	return getSection(positionSection, modele.value.rapport.sectionPrincipale);
}

function trouverElementEtParentCible(elementEtParent, event) {
	var direction;
	var distance = 1;
	switch(event.key) {
		case 'ArrowUp':
			direction = 'haut';
			break;
		case 'ArrowLeft':
			direction = 'gauche';
			break;
		case 'ArrowDown':
			direction = 'bas';
			break;
		case 'ArrowRight':
			direction = 'droite';
			break;
		case 'PageDown':
			direction = 'bas';
			distance = 10;
			break;
		case 'PageUp':
			direction = 'haut';
			distance = 10;
			break;
	}
	if (!event.ctrlKey) {
		return trouverElementFrere(elementEtParent, direction, distance, modele.value?.rapport);
	} else {
		return trouverElementFrere(elementEtParent, direction, -1, modele.value?.rapport);
	}
}

function getElementParent(element) {
	return trouverElementParent({element: element, parent: element.elementParent}, modele.value?.rapport);
}

function trouverElement(positionSection, positionDansSection) {
	return getElement(positionSection, positionDansSection, modele.value?.rapport);
}

function trouverElementEtParent(positionSection, positionDansSection) {
	return getElementEtParent(positionSection, positionDansSection, modele.value?.rapport);
}

function getMisesEnForme(typeElement) {
	switch(typeElement) {
		case 'ZONE':
		case 'TEXTE':
		case 'TABLEAU':
		case 'GRAPHIQUE':
		case 'SELECTEUR':
			return modele.value?.rapport.misesEnForme.ELEMENT_RAPPORT;
		case 'EnteteRegroupement':
		case 'EnteteAgregatGroupe':
		case 'EnteteAgregatTotal':
		case 'EnteteExpression':
		case 'EnteteVariable':
		case 'EnteteValeurVariable':
		case 'EnteteObjetsBloc':
		case 'EnteteObjet':
		case 'EnteteValeurGroupe':
		case 'ValeurExpression':
		case 'ValeurAgregatGroupe':
		case 'ValeurAgregatTotal':
			return modele.value?.rapport.misesEnForme.CELLULE_TABLEAU;
		default:
			return [];
	}
}

function donnerFocusAuRapport() {
	modele.value.rapport.focus = true;
}

function ouvrirFenetreFormule(conteneurFormule, modeEdition) {
	if (!conteneursFormulesAffichees.value.includes(conteneurFormule)) {
		const indicePremiereFenetreDispo = conteneursFormulesAffichees.value.findIndex(conteneur => conteneur === null);
		if (indicePremiereFenetreDispo !== -1) {
			conteneursFormulesAffichees.value[indicePremiereFenetreDispo] = conteneurFormule;
		} else {
			conteneursFormulesAffichees.value.push(conteneurFormule);
		}
	}
	nextTick(() => {
		conteneurFormule.formule.demandeFocus = {modeEdition: modeEdition};
	});
}

function fermerFenetreFormule(conteneurFormule) {
	const indiceConteneurFormule = conteneursFormulesAffichees.value.indexOf(conteneurFormule);
	if (indiceConteneurFormule !== -1) {
		if (indiceConteneurFormule !== conteneursFormulesAffichees.value.length - 1) {
			conteneursFormulesAffichees.value[indiceConteneurFormule] = null;
		} else {
			conteneursFormulesAffichees.value.splice(indiceConteneurFormule, 1);
		}
	}
}

function deriverOuDupliquerJeu(choix) {
	typeSourceJeuDonnees.value = choix;
}

provide('modele', modele);
provide('evenement', evenement);
provide('schema', schema);
provide('typesObjetUniques', typesObjetUniques);
provide('typesObjetMultiples', typesObjetMultiples);
provide('getTypeDonnee', getTypeDonnee);
provide('typesDonneePrimitifs', typesDonneePrimitifs);
provide('typesDonneeObjets', typesDonneeObjets);
provide('typesDonneeListesValeurs', typesDonneeListesValeurs);
provide('dimensions', dimensions);
provide('getDimension', getDimension);
provide('getTypeObjet', getTypeObjet);
provide('getListeValeurs', getListeValeurs);
provide('getParametre', getParametre);
provide('getChamp', getChamp);
provide('getChampsCles', getChampsCles);
provide('typeObjetACleAuto', typeObjetACleAuto);
provide('getObjets', getObjets);
provide('getImport', getImport);
provide('getImportFichier', getImportFichier);
provide('getTypeFormat', getTypeFormat);
provide('getTypesFormat', getTypesFormat);
provide('selectionnerSection', selectionnerSection);
provide('selectionnerChamp', selectionnerChamp);
provide('selectionnerListeValeurs', selectionnerListeValeurs);
provide('selectionnerImport', selectionnerImport);
provide('selectionnerRoutine', selectionnerRoutine);
provide('selectionnerCalculable', selectionnerCalculable);
provide('trouverElementEtParent', trouverElementEtParent);
provide('editerExpression', editerExpression);
provide('ouvrirFenetreFormule', ouvrirFenetreFormule);
provide('numOngletsSchemaSelectionnes', numOngletsSchemaSelectionnes);
provide('affichageElementsMasques', affichageElementsMasques);
provide('selectionSchema', selectionSchema);
provide('valeurSelectionnee', valeurSelectionnee);
provide('sectionSelectionnee', sectionSelectionnee);
provide('elementsSelectionnes', elementsSelectionnes);
provide('calculablesSelectionnes', calculablesSelectionnes);
provide('premierElementSelectionne', premierElementSelectionne);
provide('dernierFichierSelectionne', dernierFichierSelectionne);
provide('selectionnerElement', selectionnerElement);
provide('selectionnerPlageElements', selectionnerPlageElements);
provide('getElementParent', getElementParent);
provide('copierElements', copierElements);
provide('couperElements', couperElements);
provide('collerElements', collerElements);
provide('supprimerElementsSelectionnes', supprimerElementsSelectionnes);
provide('basculerSelection', basculerSelection);
provide('appelerApiModele', appelerApiModele);
provide('appelerApiModifModele', appelerApiModifModele);
provide('appelerApiTelechargementModele', appelerApiTelechargementModele);
provide('gererToucheClavierRapport', gererToucheClavierRapport);
provide('getMisesEnForme', getMisesEnForme);
provide('couleurs', couleurs);
provide('classeMasquage', classeMasquage);
provide('donnerFocusAuRapport', donnerFocusAuRapport);
provide('annulerDerniereCommande', annulerDerniereCommande);
provide('refaireProchaineCommande', refaireProchaineCommande);
provide('ouvrirJeuDonnees', ouvrirJeuDonnees);
provide('enregistrerSous', enregistrerSous);

</script>

<template>
	<div class="conteneur-global" 
    v-if="modele != null"
    @keydown="gererToucheClavierModele($event)">
      	<PanelGauche :schema="modele.schema"/>
      	<PanelCentre :modele="modele"/>
      	<PanelDroit 
	  		:donnees="modele.donnees" />
		<template v-for="conteneurFormule in conteneursFormulesAffichees">
			<FenetreFormule v-if="conteneurFormule"
				:conteneur-formule="conteneurFormule"
				@fermer="fermerFenetreFormule(conteneurFormule)" />
		</template>
		<ModaleDefinitionJeuDonnees 
			v-model:visible="affichageModaleDefinitionJeuDonnees"
			:jeu-donnees="modele.donnees.jeuDonneesCourant"
			@choix="choix => deriverOuDupliquerJeu(choix)" />
		<ModaleDerivationJeuDonnees 
			:visible="typeSourceJeuDonnees != null"
			:jeu-donnees="modele.donnees.jeuDonneesCourant"
			:type-source="typeSourceJeuDonnees"
			:campagnes="modele.donnees.valeursJeuxExistants.campagnes"
			@fermeture="typeSourceJeuDonnees = null" />
	</div>
</template>

<style scoped>

.conteneur-global {
	display: flex;
	height: 92vh;
	width: 100vw;
	align-items: stretch;
}

</style>